/* NPFC production runtime.
 * Loaded on every page (see snippets/npfc-styles.liquid). Guarded so that a
 * second <script> tag for this file (the tractor section still includes one)
 * is a harmless no-op instead of a redeclaration error.
 *
 * What it does:
 *  - scroll reveals: ONLY items that start below the fold are hidden, then eased
 *    in as they arrive. Above-the-fold content is never touched, so there is no
 *    flash. Reduced-motion visitors get no hiding and no movement at all.
 *  - carousel dots for any [data-npfc-carousel] track
 *  - PDP quantity tiers (drive Horizon's quantity selector, delivery-aware notes)
 *  - one-tap "add" buttons in the cart free-delivery nudge
 *  - the tractor run-once (used by sections/npfc-tractor.liquid)
 */
window.NPFC_RT = window.NPFC_RT || (() => {
  const prefersReduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // selector, stagger step in ms
  const REVEAL_GROUPS = [
    ['.npfc-cards > .npfc-card', 70],
    ['.npfc-pick > a', 60],
    ['.npfc-bundles .bundle-grid > .card', 90],
    ['.npfc-stories .story-grid > div', 110],
    ['.npfc-editorial .row', 0],
    ['.npfc-faq .faq-item', 40],
    ['.npfc-loop > li', 70],
    ['.npfc-guide-grid .card, .npfc-guide-grid a', 60],
    ['.npfc-facts > div, .hero .facts > div', 70],
    ['.npfc-story-beat', 0],
    ['.npfc-callout', 0],
    ['.npfc-pull', 0],
    ['.npfc-compare', 0],
    ['.npfc-steps li', 80]
  ];

  function reveals(root) {
    root = root || document;
    if (prefersReduced() || !('IntersectionObserver' in window)) return;
    const h = window.innerHeight || 800;
    const targets = [];
    REVEAL_GROUPS.forEach(([sel, step]) => {
      root.querySelectorAll(sel).forEach((el, i) => {
        if (el.hasAttribute('data-reveal') || el.dataset.npfcRevealed) return;
        const par = el.parentElement;
        if (par && par.hasAttribute('data-npfc-carousel') && par.scrollWidth > par.clientWidth + 2) return; // swipe tracks stay visible
        if (el.getBoundingClientRect().top < h * 0.96) return; // already on screen: leave alone
        el.setAttribute('data-reveal', String((i % 5) * step));
        targets.push(el);
      });
    });
    if (!targets.length) return;
    document.documentElement.classList.add('js-reveal');
    const io = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (!e.isIntersecting) return;
        const el = e.target;
        io.unobserve(el);
        const delay = parseFloat(el.getAttribute('data-reveal')) || 0;
        setTimeout(() => {
          el.classList.add('is-revealed');
          // hand the element back to its normal styles once it has settled, so
          // hover/press transitions on cards and buttons are not slowed down
          setTimeout(() => { el.removeAttribute('data-reveal'); el.classList.remove('is-revealed'); el.dataset.npfcRevealed = '1'; }, 900);
        }, delay);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.06 });
    targets.forEach(el => io.observe(el));
  }

  function carousels(root) {
    (root || document).querySelectorAll('[data-npfc-carousel]').forEach(track => {
      if (track.dataset.npfcDots) return;
      const items = Array.from(track.children);
      if (items.length < 2) return;
      track.dataset.npfcDots = '1';
      const dots = document.createElement('div');
      dots.className = 'npfc-dots';
      dots.setAttribute('aria-hidden', 'true');
      items.forEach(() => dots.appendChild(document.createElement('span')));
      track.insertAdjacentElement('afterend', dots);
      let raf = 0;
      const update = () => {
        raf = 0;
        const mid = track.scrollLeft + track.clientWidth / 2;
        let best = 0, bestD = Infinity;
        items.forEach((it, i) => {
          const d = Math.abs(it.offsetLeft + it.offsetWidth / 2 - mid);
          if (d < bestD) { bestD = d; best = i; }
        });
        // at the very end of the track, light the last dot
        if (track.scrollLeft + track.clientWidth >= track.scrollWidth - 4) best = items.length - 1;
        if (track.scrollLeft <= 2) best = 0;
        Array.from(dots.children).forEach((d, i) => d.classList.toggle('on', i === best));
      };
      track.addEventListener('scroll', () => { if (!raf) raf = requestAnimationFrame(update); }, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  function tractorRun(strip) {
    if (prefersReduced()) { strip.classList.add('settled'); return; }
    const run = () => {
      strip.classList.add('running');
      strip.addEventListener('animationend', () => strip.classList.add('settled'), { once: true });
    };
    const io = new IntersectionObserver(es => {
      es.forEach(e => { if (e.isIntersecting) { run(); io.disconnect(); } });
    }, { threshold: 0.35 });
    io.observe(strip);
  }


  // ---------------------------------------------------------------- quantity tiers
  const money = c => '£' + (c / 100).toFixed(2);
  const track = (name, params) => { if (typeof window.npfcTrack === 'function') window.npfcTrack(name, params || {}); };

  function tiers(root) {
    (root || document).querySelectorAll('[data-npfc-tiers]').forEach(box => {
      if (box.dataset.ready) return;
      box.dataset.ready = '1';
      let variants = {};
      try { variants = JSON.parse(box.querySelector('[data-npfc-tier-variants]').textContent); } catch (e) {}
      const threshold = Number(box.dataset.threshold) || 3499;
      const inputs = Array.from(box.querySelectorAll('input[type="radio"]'));
      let cartTotal = 0;

      let giftMap = {};
      try { giftMap = JSON.parse(box.querySelector('[data-npfc-tier-gift-map]').textContent); } catch (e) {}
      const currentVid = () => {
        const chosen = document.querySelector('variant-picker input[type="radio"]:checked');
        return chosen && chosen.dataset.variantId ? chosen.dataset.variantId : box.dataset.variantId;
      };
      const unitPrice = () => Number(variants[currentVid()]) || Number(box.dataset.unit);
      const render = () => {
        const unit = unitPrice();
        // the free Tend hint only shows for variants that take part (e.g. not the Soil Improver 17L)
        box.querySelectorAll('.npfc-tier__gift').forEach(el => { el.hidden = giftMap[currentVid()] === false; });
        inputs.forEach(inp => {
          const n = Number(inp.value);
          const tier = inp.closest('.npfc-tier');
          tier.querySelector('[data-total]').textContent = money(unit * n);
          const gap = threshold - (cartTotal + unit * n);
          const note = tier.querySelector('[data-note]');
          if (gap <= 0) { note.textContent = box.dataset.unlockedText; note.classList.add('is-free'); }
          else { note.textContent = money(gap) + ' ' + box.dataset.gapText; note.classList.remove('is-free'); }
        });
      };
      const setQty = n => {
        const sel = document.querySelector('quantity-selector-component');
        if (sel && typeof sel.setValue === 'function') { sel.setValue(n); return; }
        const i = document.querySelector('quantity-selector-component input[name="quantity"]');
        if (i) { i.value = String(n); i.dispatchEvent(new Event('change', { bubbles: true })); }
      };
      const refreshCart = () => fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(r => r.json()).then(c => { cartTotal = Number(c.total_price) || 0; render(); }).catch(render);

      box.addEventListener('change', e => {
        if (!e.target.matches('input[type="radio"]')) return;
        const n = Number(e.target.value);
        box.classList.remove('is-custom');
        setQty(n);
        track('quantity_tier_select', { quantity: n, product: document.querySelector('product-form-component')?.dataset.productId || '' });
      });
      // keep the tiers honest if someone uses the native stepper instead
      document.addEventListener('quantity-selector:update', e => {
        const q = e.detail && e.detail.quantity;
        if (!q) return;
        let hit = false;
        inputs.forEach(i => { const on = Number(i.value) === q; i.checked = on; hit = hit || on; });
        box.classList.toggle('is-custom', !hit);
      });
      document.addEventListener('change', e => {
        if (e.target.closest && e.target.closest('variant-picker')) setTimeout(render, 80);
      });
      document.addEventListener('click', e => {
        if (e.target.closest && e.target.closest('.add-to-cart-button')) setTimeout(refreshCart, 1400);
      });
      render();
      refreshCart();
    });
  }

  // ---------------------------------------------------------------- cart nudge: one-tap add
  // Cart drawer/page content is re-rendered by Horizon's own section renderer, so this is a
  // single delegated handler. It adds through the same endpoint and event Horizon's product
  // form uses, so the drawer, the free-delivery bar and the header count all update natively.
  async function nudgeAdd(btn) {
    if (btn.dataset.state === 'adding') return;
    btn.dataset.state = 'adding';
    const label = btn.textContent;
    btn.textContent = 'Adding';
    try {
      const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map(c => c.dataset && c.dataset.sectionId).filter(Boolean);
      const addUrl = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add';
      const res = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(btn.dataset.variantId), quantity: 1 }], sections: sectionIds.join(',') })
      }).then(r => r.json());
      if (res.status) throw new Error(res.message || 'add failed');
      const cart = await fetch('/cart.json', { headers: { Accept: 'application/json' } }).then(r => r.json());
      const ev = await import('@shopify/events');
      const deferred = ev.CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(new ev.CartLinesUpdateEvent({
        action: 'add', context: 'product',
        lines: [{ merchandiseId: String(btn.dataset.variantId), quantity: 1 }],
        promise: deferred.promise
      }));
      deferred.resolve({
        cart: ev.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'npfc-nudge', sourceId: 'npfc-nudge', itemCount: 1, sections: res.sections, didError: false }
      });
      track(btn.dataset.npfcEventName || 'free_shipping_upsell_add', { handle: btn.dataset.handle || '', subtotal_pence: cart.total_price });
    } catch (err) {
      btn.textContent = label;
      btn.dataset.state = '';
      window.location.href = btn.dataset.fallback || '/cart';
    }
  }
  document.addEventListener('click', e => {
    const btn = e.target.closest && e.target.closest('[data-npfc-nudge-add]');
    if (!btn) return;
    e.preventDefault();
    nudgeAdd(btn);
  });

  // The product gallery scrolls sideways, so it has to be reachable by keyboard (axe: scrollable-region-focusable).
  // Focusing it lets the arrow keys scroll the snap points; the previous and next buttons and thumbnails are unchanged.
  function gallery(root) {
    (root || document).querySelectorAll('media-gallery slideshow-slides').forEach(s => {
      if (s.dataset.npfcKb) return;
      s.dataset.npfcKb = '1';
      s.setAttribute('tabindex', '0');
      s.setAttribute('role', 'region');
      s.setAttribute('aria-label', 'Product images');
    });
  }

  function init(root) { reveals(root); carousels(root); tiers(root); gallery(root); }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  // theme editor / section re-render
  document.addEventListener('shopify:section:load', e => init(e.target));

  return { prefersReduced, reveals, carousels, tiers, tractorRun, init };
})();
